import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateCourseDetailsMutation, useUploadPreviewVideoMutation, useSubmitCourseForReviewMutation } from '../../../api/apiSlice';
import Spinner from '../../common/Spinner';
import { toast } from 'react-hot-toast';

// isLocked = курс active/pending_review/archived.
//   • language / telegram / whatsapp — РЕДАКТИРУЕМЫ (бэк разрешает)
//   • Превью-видео блокируется (визуальная подмена продукта)
//   • «Отправить на проверку» прячется (уже отправлен / на ревью / опубликован)
const CourseSettingsForm = ({ course, isLocked = false }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    language: 'Русский',
    telegramGroupUrl: '',
    whatsappGroupUrl: '',
    previewVideoKey: '',
  });
  const [selectedLessonVideo, setSelectedLessonVideo] = useState('');
  const [currentVideoName, setCurrentVideoName] = useState(''); // Для отображения имени файла
  const fileInputRef = useRef(null);

  const [updateDetails, { isLoading: isUpdating }] = useUpdateCourseDetailsMutation();
  const [uploadPreviewVideo, { isLoading: isUploading }] = useUploadPreviewVideoMutation();
  const [submitCourseForReview, { isLoading: isSubmitting }] = useSubmitCourseForReviewMutation();

  // Логирование для отладки
  useEffect(() => {
    console.log('Course data in SettingsForm:', JSON.stringify(course, null, 2));
    if (course) {
      const allLessons = course.sections?.flatMap((section) => section.lessons) || [];
      console.log('All lessons:', allLessons);
      const lessonsWithVideo = allLessons.filter((lesson) => lesson.videoKey);
      console.log('Lessons with videoKey:', lessonsWithVideo);
    }
  }, [course]);

  // Инициализация formData и currentVideoName
  useEffect(() => {
    if (course) {
      setFormData({
        language: course.language || 'Русский',
        telegramGroupUrl: course.telegramGroupUrl || '',
        whatsappGroupUrl: course.whatsappGroupUrl || '',
        previewVideoKey: course.previewVideoKey || '',
      });
      if (course.previewVideoKey) {
        setSelectedLessonVideo('custom');
        // Сначала ищем урок по videoKey — у него есть человеческое название.
        // Если это кастомный загруженный файл (не привязан к уроку) —
        // фолбэк на хвост ключа.
        const allLessons = course.sections?.flatMap((section) => section.lessons) || [];
        const matchingLesson = allLessons.find((lesson) => lesson.videoKey === course.previewVideoKey);
        setCurrentVideoName(
          matchingLesson?.title
          || matchingLesson?.originalVideoName
          || course.previewVideoKey.split('/').pop()
          || 'Загруженное видео',
        );
      } else {
        const firstLessonWithVideo = course.sections
          ?.flatMap((section) => section.lessons)
          .find((lesson) => lesson.videoKey);
        if (firstLessonWithVideo) {
          setSelectedLessonVideo(firstLessonWithVideo._id);
          setFormData((prev) => ({ ...prev, previewVideoKey: firstLessonWithVideo.videoKey || '' }));
          setCurrentVideoName(
            firstLessonWithVideo.title
            || firstLessonWithVideo.originalVideoName
            || firstLessonWithVideo.videoKey.split('/').pop(),
          );
        } else {
          setSelectedLessonVideo('');
          setCurrentVideoName('');
        }
      }
    }
    // Зависимость по course.id, а НЕ по всему объекту course. RTK Query
    // после успешного uploadPreviewVideo инвалидирует кэш и подтягивает
    // обновлённый course — раньше это перезаписывало formData, и любые
    // изменения, которые юзер набрал, но не нажал «Сохранить», стирались.
    // Теперь форма реинициализируется только при смене редактируемого курса.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?._id || course?.id]);

  // Обработчик изменений полей
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Обработчик выбора видео
  const handleVideoSelection = (e) => {
    const value = e.target.value;
    console.log('Selected video value:', value);
    setSelectedLessonVideo(value);
    if (value === 'custom') {
      console.log('Selected custom video');
      setFormData({ ...formData, previewVideoKey: course.previewVideoKey || '' });
      setCurrentVideoName(course.previewVideoKey ? course.previewVideoKey.split('/').pop() : 'Custom Video');
    } else if (value) {
      const allLessons = course.sections?.flatMap((section) => section.lessons) || [];
      const selectedLesson = allLessons.find((lesson) => lesson._id === value);
      console.log('Selected lesson:', selectedLesson);
      const newVideoKey = selectedLesson?.videoKey || '';
      const newVideoName = selectedLesson?.originalVideoName || newVideoKey.split('/').pop() || '';
      console.log('Setting previewVideoKey to:', newVideoKey);
      console.log('Setting currentVideoName to:', newVideoName);
      setFormData({ ...formData, previewVideoKey: newVideoKey });
      setCurrentVideoName(newVideoName);
    } else {
      console.log('No video selected');
      setFormData({ ...formData, previewVideoKey: '' });
      setCurrentVideoName('');
    }
  };

  // Обработчик загрузки видео
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    console.log('Uploading video:', file.name);
    try {
      const response = await uploadPreviewVideo({ courseId: course._id, videoFile: file }).unwrap();
      console.log('Upload preview video response:', response);
      toast.success('Превью-видео загружено');
      setSelectedLessonVideo('custom');
      setFormData({ ...formData, previewVideoKey: response.previewVideoKey || course.previewVideoKey });
      setCurrentVideoName(file.name); // Отображаем имя загруженного файла
    } catch (error) {
      console.error('Upload preview video error:', error);
      toast.error(error.data?.message || 'Не удалось загрузить видео');
    }
  };

  // Обработчик отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    // В locked-режиме не шлём previewVideoKey — бэк всё равно вернёт 403.
    const payload = isLocked
      ? {
          language: formData.language,
          telegramGroupUrl: formData.telegramGroupUrl,
          whatsappGroupUrl: formData.whatsappGroupUrl,
        }
      : formData;
    try {
      await updateDetails({ courseId: course._id, ...payload }).unwrap();
      toast.success('Настройки курса обновлены');
    } catch (error) {
      toast.error(error.data?.message || 'Ошибка при обновлении настроек');
    }
  };

  // Обработчик отправки на проверку.
  // После успеха редиректим на список курсов — раньше юзер оставался на
  // editor'е и не понимал что произошло. К тому же на pending_review editor
  // покажет safe-panel (мы теперь блокируем структуру) — это путало.
  const handleSubmitForReview = async () => {
    try {
      await submitCourseForReview(course._id).unwrap();
      toast.success('Курс отправлен на проверку. Ждите решение модератора.');
      navigate('/dashboard/instructor/my-courses');
    } catch (error) {
      console.error('Submit for review error:', error);
      toast.error(error.data?.message || 'Ошибка при отправке на проверку');
    }
  };

  // Получение всех видеоуроков. Лейбл — название урока (то, что инструктор
  // сам ввёл и понимает), а не оригинальное имя файла "IMG_7272.MOV" и тем
  // более не хэшированный videoKey. Файл-имя оставляем как fallback на случай
  // безымянного урока.
  const lessonVideos = course.sections
    ?.flatMap((section) => section.lessons)
    .filter((lesson) => lesson.videoKey)
    .map((lesson) => ({
      value: lesson._id,
      label: lesson.title || lesson.originalVideoName || lesson.videoKey.split('/').pop() || 'Без названия',
    })) || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Настройки публикации</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Язык курса */}
        <div>
          <label htmlFor="language" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
            Язык курса
          </label>
          <select
            name="language"
            value={formData.language}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition bg-transparent text-gray-900 dark:text-gray-100"
          >
            <option value="Русский">Русский</option>
            <option value="Казахский">Казахский</option>
            <option value="Английский">Английский</option>
          </select>
        </div>

        {/* Telegram группа */}
        <div>
          <label
            htmlFor="telegramGroupUrl"
            className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1"
          >
            Ссылка на Telegram группу
          </label>
          <input
            type="url"
            name="telegramGroupUrl"
            value={formData.telegramGroupUrl}
            onChange={handleChange}
            placeholder="https://t.me/..."
            className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition bg-transparent"
          />
        </div>

        {/* WhatsApp группа */}
        <div>
          <label
            htmlFor="whatsappGroupUrl"
            className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1"
          >
            Ссылка на WhatsApp группу
          </label>
          <input
            type="url"
            name="whatsappGroupUrl"
            value={formData.whatsappGroupUrl}
            onChange={handleChange}
            placeholder="https://chat.whatsapp.com/..."
            className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition bg-transparent"
          />
        </div>

        {/* Превью-видео — скрываем целиком на locked-курсе. Бэк всё равно
            вернёт 403 на любую попытку, нет смысла рисовать неактивный UI. */}
        {!isLocked && (
          <div>
            <label
              htmlFor="previewVideo"
              className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1"
            >
              Видео для превью
            </label>
            {lessonVideos.length > 0 || course.previewVideoKey ? (
              <select
                id="previewVideo"
                value={selectedLessonVideo}
                onChange={(e) => handleVideoSelection(e)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition bg-transparent text-gray-900 dark:text-gray-100"
              >
                <option value="">Выберите видео</option>
                {lessonVideos.map((video) => (
                  <option key={video.value} value={video.value}>
                    {video.label}
                  </option>
                ))}
                <option value="custom">Загрузить новое видео</option>
              </select>
            ) : (
              <p className="text-gray-500">Нет доступных видеоуроков. Добавьте уроки с видео в "Учебная программа".</p>
            )}
            {selectedLessonVideo === 'custom' && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  disabled={isUploading}
                >
                  {isUploading ? 'Загрузка...' : 'Загрузить видео'}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleVideoUpload}
                  className="hidden"
                  accept="video/*"
                />
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Текущее видео: <strong>{currentVideoName || 'Не выбрано'}</strong>
            </p>
            {course.previewVideoUrl && (
              <p className="text-xs text-gray-500 mt-1">
                Просмотреть: <a href={course.previewVideoUrl} target="_blank" rel="noopener noreferrer">Открыть</a>
              </p>
            )}
          </div>
        )}
      </div>

      <div className="border-t dark:border-slate-700 pt-6 flex justify-between">
        <button
          type="submit"
          disabled={isUpdating}
          className="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition disabled:opacity-50"
        >
          {isUpdating ? 'Сохранение...' : 'Сохранить настройки'}
        </button>
        {/* «Отправить на проверку» имеет смысл только на draft.
            На rejected отправляем после правок (тоже draft по сути), на
            active/pending_review/archived — скрываем. */}
        {!isLocked && course.status === 'draft' && (
          <button
            type="button"
            onClick={handleSubmitForReview}
            disabled={isSubmitting}
            className="px-6 py-2.5 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition disabled:opacity-50"
          >
            {isSubmitting ? 'Отправка...' : 'Отправить на проверку'}
          </button>
        )}
      </div>
    </form>
  );
};

export default CourseSettingsForm;